// Style primitives shared across pages. Designed to lock visual rhythm in
// one place — change here, every page follows.

import type { ReactNode, SVGProps, ComponentType } from 'react';

export function PageHeader({
  title, subtitle, actions, icon: Icon,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  icon?: ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;
}) {
  return (
    <div className="flex items-end justify-between gap-6 mb-8">
      <div className="flex items-start gap-4">
        {Icon && (
          <div className="hidden sm:flex h-11 w-11 items-center justify-center rounded-xl
                          bg-gradient-to-br from-accent-500 to-accent-700 text-white shadow-sm shadow-accent-500/30">
            <Icon size={20} />
          </div>
        )}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}>
      {children}
    </div>
  );
}

export function KPI({
  label, value, hint, tone = 'default', icon: Icon,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'default' | 'good' | 'warn' | 'bad';
  icon?: ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;
}) {
  const toneCls = {
    default: 'text-slate-900',
    good:    'text-emerald-600',
    warn:    'text-amber-600',
    bad:     'text-rose-600',
  }[tone];
  const ringCls = {
    default: 'ring-slate-200',
    good:    'ring-emerald-200',
    warn:    'ring-amber-200',
    bad:     'ring-rose-200',
  }[tone];
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
          <p className={`text-2xl font-semibold mt-2 tabular-nums ${toneCls}`}>{value}</p>
          {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
        </div>
        {Icon && (
          <div className={`h-9 w-9 rounded-lg bg-slate-50 ring-1 ${ringCls} ${toneCls} flex items-center justify-center shrink-0`}>
            <Icon size={16} />
          </div>
        )}
      </div>
    </Card>
  );
}

export function FilterBar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {children}
    </div>
  );
}

export const inputCls =
  'h-9 border border-slate-300 rounded-lg px-3 text-sm bg-white shadow-sm ' +
  'focus:outline-none focus:ring-4 focus:ring-accent-500/15 focus:border-accent-500 transition';

export const selectCls = inputCls + ' pr-8 cursor-pointer';

export const btnPrimary =
  'h-9 inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white px-4 rounded-lg ' +
  'text-sm font-medium hover:bg-slate-800 active:bg-slate-950 transition shadow-sm disabled:opacity-50';

export const btnAccent =
  'h-9 inline-flex items-center justify-center gap-1.5 bg-accent-600 text-white px-4 rounded-lg ' +
  'text-sm font-medium hover:bg-accent-700 transition shadow-sm shadow-accent-500/20 disabled:opacity-50';

export const btnGhost =
  'h-9 inline-flex items-center justify-center gap-1.5 border border-slate-300 bg-white px-3 rounded-lg ' +
  'text-sm text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition';

export const th = 'text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-4 py-3';
export const td = 'px-4 py-3 align-middle text-sm';
export const trBase = 'border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors';

export function EmptyState({
  children, icon: Icon,
}: {
  children: ReactNode;
  icon?: ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;
}) {
  return (
    <div className="px-4 py-16 text-center">
      {Icon && (
        <div className="mx-auto h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
          <Icon size={20} />
        </div>
      )}
      <p className="text-slate-400 text-sm">{children}</p>
    </div>
  );
}

export function KindPill({ kind }: { kind: string }) {
  const map: Record<string, string> = {
    runtime: 'bg-rose-50 text-rose-700 ring-rose-200',
    react:   'bg-violet-50 text-violet-700 ring-violet-200',
    promise: 'bg-amber-50 text-amber-700 ring-amber-200',
    network: 'bg-sky-50 text-sky-700 ring-sky-200',
  };
  const cls = map[kind] ?? 'bg-slate-50 text-slate-700 ring-slate-200';
  return (
    <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ring-1 ${cls}`}>
      {kind}
    </span>
  );
}
